import { faChevronLeft } from '@fortawesome/pro-light-svg-icons';
import classNames from 'classnames';
import React from 'react';
import { Button } from '../../flayr-components/Button/Button';
import { Heading } from '../../flayr-components/Heading/Heading';
import useScrollLock from '../../hooks/useScrollLock';
import IconBtn from '../../lib/IconBtn/IconBtn';
import Portal from '../../lib/Portal/Portal';
import FooterCta from '../FooterCta/FooterCta';
import DrawerSheetHeader from '../DrawerSheetHeader/DrawerSheetHeader';

interface IFormWizardProps extends React.HTMLAttributes<HTMLDivElement> {
  open?: boolean;
  steps?: string[];
  activeStep?: string;
  titleClassName?: string;
  subTitleClassName?: string;
  title?: string;
  subTitle?: string;
  nextBtnLabel?: string;
  nextBtnDisabled?: boolean;
  className?: string;
  hideClearBtn?: boolean;
  hideBackBtn?: boolean;
  loading?: boolean;
  drawerView?: boolean;
  hideFooter?: boolean;
  nextBtnInHeader?: boolean;
  nextBtnClassName?: string;
  clearBtnLabel?: string;
  scrollToTopOnNext?: boolean;
  scrollToTopOnClear?: boolean;
  footerCtaContainerProps?: any;
  onClear?: (e?: any) => void;
  onNext?: (e?: any) => void;
  onBack?: (e: any) => void;
  onClose?: (e?: any) => void;
}

const FormWizard: React.FC<
  React.PropsWithChildren<React.PropsWithChildren<IFormWizardProps>>
> = ({
  open,
  title,
  hideClearBtn,
  hideBackBtn,
  subTitle,
  children,
  nextBtnLabel = 'Next',
  nextBtnDisabled,
  subTitleClassName = '',
  titleClassName,
  className,
  loading,
  drawerView,
  clearBtnLabel = 'Clear',
  onClear,
  onNext,
  onClose,
  onBack,
  hideFooter,
  nextBtnInHeader,
  nextBtnClassName,
  scrollToTopOnNext,
  scrollToTopOnClear,
  footerCtaContainerProps,
  ...props
}) => {
  useScrollLock(open);
  const containerRef = React.useRef<HTMLDivElement>(null);

  function handleNext() {
    if (scrollToTopOnNext) {
      setTimeout(() => {
        if (containerRef.current) containerRef.current.scrollTo(0, 0);
      }, 0);
    }
    onNext?.();
  }

  function handleClear() {
    if (scrollToTopOnClear) {
      setTimeout(() => {
        if (containerRef.current) containerRef.current.scrollTo(0, 0);
      }, 0);
    }
    onClear?.();
  }

  if (!open) {
    return null;
  }

  const Title = (
    <Heading
      tag="h2"
      bold="medium"
      className={classNames(
        'mt-2 text-4xl text-white xs:mt-0 xs:text-2xl',
        titleClassName
      )}
    >
      {title}
    </Heading>
  );

  const SubTitle = (
    <Heading
      tag="h3"
      bold="medium"
      className={classNames(
        `text-3xl text-primary xs:text-xl`,
        subTitleClassName
      )}
    >
      {subTitle}
    </Heading>
  );
  return (
    <>
      <Portal className={className}>
        {!drawerView && (
          <div
            {...props}
            ref={containerRef}
            className="fixed inset-0 z-30 overflow-y-auto bg-white"
          >
            <section
              data-testid="wizard-header"
              className="sticky top-0 p-4 pb-8 bg-black"
            >
              <div className="flex items-center justify-between w-full">
                <div>
                  {!hideBackBtn && (
                    <IconBtn
                      onClick={onBack}
                      color="plain"
                      icon={faChevronLeft}
                      round
                      className="pl-0 pr-0 w-7 h-7"
                    />
                  )}
                </div>

                {nextBtnInHeader && (
                  <Button
                    color="hot-pink"
                    round
                    disabled={nextBtnDisabled}
                    loading={loading}
                    className={classNames('pl-8 pr-8', nextBtnClassName)}
                    onClick={handleNext}
                  >
                    {nextBtnLabel}
                  </Button>
                )}
              </div>

              <section className="w-full pb-4 mt-8 xs:pb-1 xs:mt-4 lg:mx-auto lg:w-2/3">
                {SubTitle}
                {Title}
              </section>
            </section>

            <div className="relative z-10 w-full px-4 pt-10 pb-20 -mt-3 bg-white rounded-t-xl">
              {/* 
                we need overflow-x-hidden because it would cause weird scroll on safari on the 2nd step 

                but doing so would cause a problem with places input not showing menu because of its absolute positioned menu
                so we had to put min-h-6/12 screen to fix the error
              */}
              <div className="w-full overflow-x-hidden lg:mx-auto lg:w-2/3 min-h-6/12-screen">
                {children}
              </div>

              {!hideFooter && !nextBtnInHeader && (
                <FooterCta
                  leftContent={
                    <Button
                      hidden={hideClearBtn}
                      onClick={handleClear}
                      className="-ml-4 underline"
                      clear
                    >
                      {clearBtnLabel}
                    </Button>
                  }
                  btnLabel={nextBtnLabel}
                  ctaClassName={classNames('pl-8 pr-8', nextBtnClassName)}
                  ctaDisabled={nextBtnDisabled}
                  ctaProps={{ loading }}
                  onCtaClick={handleNext}
                  containerProps={footerCtaContainerProps}
                  hideCtaBtn={nextBtnInHeader}
                />
              )}
            </div>
          </div>
        )}
      </Portal>
      {drawerView && (
        <div className={classNames('flex flex-col w-full h-full bg-white', className)}>
          <DrawerSheetHeader
            titleAlign="left"
            className="!h-auto min-h-14 items-center py-2.5"
            title={
              <p className="truncate font-instrument-serif text-[24px] font-normal leading-none text-gray-900">
                {title}
              </p>
            }
            onBack={!hideBackBtn && onBack ? () => onBack(undefined) : undefined}
            backAriaLabel="Back"
            onClose={onClose ? () => onClose() : () => {}}
            closeAriaLabel="Close"
          />

          <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-8 pt-4 text-gray-900">
            {children}
          </div>

          {!hideFooter && (
            <div
              data-testid="search-wizard-footer-drawer"
              className="shrink-0 px-5 pb-6 pt-3"
            >
              <div className="flex items-center justify-between gap-3">
                {!hideClearBtn && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="text-sm font-medium text-gray-500 underline underline-offset-2"
                  >
                    {clearBtnLabel}
                  </button>
                )}
                <Button
                  block={hideClearBtn}
                  shadow
                  color="hot-pink"
                  size="lg"
                  className={classNames(
                    'ml-auto pl-10 pr-10',
                    nextBtnClassName
                  )}
                  disabled={nextBtnDisabled}
                  loading={loading}
                  onClick={handleNext}
                >
                  {nextBtnLabel}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};
export default FormWizard;
